import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(5px);
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  opacity: ${props => props.isOpen ? 1 : 0};
  transition: all 0.3s ease;
`;

const ModalContainer = styled.div`
  width: 95vw;
  max-width: 400px;
  max-height: 85vh;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  border-radius: 15px;
  padding: 0;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.9)'};
  transition: transform 0.3s ease;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  color: white;
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ModalBody = styled.div`
  padding: 1rem;
  max-height: 65vh;
  overflow-y: auto;
`;

const ColumnList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ColumnItem = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ColumnInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ColumnName = styled.div`
  color: white;
  font-weight: 500;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const ColumnDetails = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
  display: flex;
  gap: 1rem;
`;

const ColumnActions = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const ActionButton = styled.button`
  padding: 0.25rem 0.5rem;
  border: none;
  border-radius: 4px;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => 
    props.variant === 'edit' ? '#ffc107' :
    props.variant === 'delete' ? '#dc3545' :
    props.variant === 'add' ? '#28a745' : '#6c757d'
  };
  color: white;
  
  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const AddColumnForm = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const FormGroup = styled.div`
  margin-bottom: 0.75rem;
`;

const FormLabel = styled.label`
  display: block;
  color: white;
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.8rem;
  box-sizing: border-box;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
  
  &:focus {
    outline: none;
    border-color: dodgerblue;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.8rem;
  box-sizing: border-box;
  
  option {
    background: #1e3c72;
    color: white;
  }
  
  &:focus {
    outline: none;
    border-color: dodgerblue;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  padding: 2rem;
  font-size: 0.9rem;
`;

const ErrorMessage = styled.div`
  background: rgba(220, 53, 69, 0.2);
  border: 1px solid rgba(220, 53, 69, 0.5);
  border-radius: 4px;
  padding: 0.5rem;
  margin-bottom: 1rem;
  color: #ff6b6b;
  font-size: 0.8rem;
`;

const MobileColumnEditModal = ({ isOpen, onClose, boardId }) => {
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newColumn, setNewColumn] = useState({
    name: '',
    type: 'VARCHAR',
    length: '255',
    nullable: true,
    defaultValue: ''
  });

  const columnTypes = [
    'VARCHAR', 'TEXT', 'INT', 'BIGINT', 'DECIMAL', 'DATETIME', 'DATE', 'BOOLEAN'
  ];

  const fetchColumns = async () => {
    if (!boardId || !isOpen) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`/request/getBoardColumns?boardId=${boardId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('컬럼 정보를 가져오는데 실패했습니다.');
      }
      
      const data = await response.json();
      setColumns(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching columns:', err);
      setError(err.message);
      setColumns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchColumns();
  }, [boardId, isOpen]);

  const handleAddColumn = async () => {
    if (!newColumn.name.trim()) {
      setError('컬럼 이름을 입력해주세요.');
      return;
    }

    try {
      const response = await fetch('/request/addBoardColumn', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          boardId,
          columnName: newColumn.name,
          columnType: newColumn.type,
          columnLength: newColumn.length,
          nullable: newColumn.nullable,
          defaultValue: newColumn.defaultValue
        })
      });

      if (!response.ok) {
        throw new Error('컬럼 추가에 실패했습니다.');
      }

      setNewColumn({
        name: '',
        type: 'VARCHAR',
        length: '255',
        nullable: true,
        defaultValue: ''
      });
      setShowAddForm(false);
      setError('');
      fetchColumns();
    } catch (err) {
      console.error('Error adding column:', err);
      setError(err.message);
    }
  };

  const handleDeleteColumn = async (columnName) => {
    if (!window.confirm(`'${columnName}' 컬럼을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const response = await fetch('/request/deleteBoardColumn', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          boardId,
          columnName
        })
      });

      if (!response.ok) {
        throw new Error('컬럼 삭제에 실패했습니다.');
      }

      fetchColumns();
    } catch (err) {
      console.error('Error deleting column:', err);
      setError(err.message);
    }
  };

  const handleClose = () => {
    setShowAddForm(false);
    setError('');
    setNewColumn({
      name: '',
      type: 'VARCHAR',
      length: '255',
      nullable: true,
      defaultValue: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleClose}>
      <ModalContainer isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>컬럼 편집</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>
        
        <ModalBody>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <ActionButton 
            variant="add" 
            onClick={() => setShowAddForm(!showAddForm)}
            style={{ marginBottom: '1rem', width: '100%' }}
          >
            {showAddForm ? '추가 취소' : '컬럼 추가'}
          </ActionButton>

          {showAddForm && (
            <AddColumnForm>
              <FormGroup>
                <FormLabel>컬럼 이름</FormLabel>
                <FormInput
                  type="text"
                  placeholder="컬럼 이름을 입력하세요"
                  value={newColumn.name}
                  onChange={(e) => setNewColumn({...newColumn, name: e.target.value})}
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel>데이터 타입</FormLabel>
                <FormSelect
                  value={newColumn.type}
                  onChange={(e) => setNewColumn({...newColumn, type: e.target.value})}
                >
                  {columnTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </FormSelect>
              </FormGroup>
              
              {(newColumn.type === 'VARCHAR' || newColumn.type === 'DECIMAL') && (
                <FormGroup>
                  <FormLabel>길이</FormLabel>
                  <FormInput
                    type="text"
                    placeholder="255"
                    value={newColumn.length}
                    onChange={(e) => setNewColumn({...newColumn, length: e.target.value})}
                  />
                </FormGroup>
              )}
              
              <FormGroup>
                <FormLabel>기본값</FormLabel>
                <FormInput
                  type="text"
                  placeholder="기본값 (선택사항)"
                  value={newColumn.defaultValue}
                  onChange={(e) => setNewColumn({...newColumn, defaultValue: e.target.value})}
                />
              </FormGroup>
              
              <ActionButton variant="add" onClick={handleAddColumn} style={{ width: '100%' }}>
                컬럼 추가
              </ActionButton>
            </AddColumnForm>
          )}

          {loading ? (
            <LoadingMessage>컬럼 정보를 불러오는 중...</LoadingMessage>
          ) : (
            <ColumnList>
              {columns.length === 0 ? (
                <LoadingMessage>컬럼 정보가 없습니다.</LoadingMessage>
              ) : (
                columns.map((column, index) => (
                  <ColumnItem key={index}>
                    <ColumnInfo>
                      <ColumnName>{column.COLUMN_NAME}</ColumnName>
                      <ColumnDetails>
                        <span>타입: {column.DATA_TYPE}</span>
                        {column.CHARACTER_MAXIMUM_LENGTH && (
                          <span>길이: {column.CHARACTER_MAXIMUM_LENGTH}</span>
                        )}
                        <span>{column.IS_NULLABLE === 'YES' ? 'NULL 허용' : 'NOT NULL'}</span>
                      </ColumnDetails>
                    </ColumnInfo>
                    <ColumnActions>
                      {!['id', 'created_at', 'updated_at'].includes(column.COLUMN_NAME.toLowerCase()) && (
                        <ActionButton 
                          variant="delete" 
                          onClick={() => handleDeleteColumn(column.COLUMN_NAME)}
                        >
                          삭제
                        </ActionButton>
                      )}
                    </ColumnActions>
                  </ColumnItem>
                ))
              )}
            </ColumnList>
          )}
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default MobileColumnEditModal;